import Fraction from 'fraction.js';

export const ingredientsUnitMapping = [
  { tsp: 'teaspoons' },
  { tbs: 'tablespoons' },
  { g: 'grams' },
  { kg: 'kilograms' },
  { l: 'liter' },
  { ml: 'milliliter' },
  { cm: 'centimeters' },
  { c: 'cups' },
  { oz: 'ounces' },
  { lb: 'pounds' },
  { gal: 'gallons' },
  { 'fl oz': 'fluid ounce' },
  { doz: 'dozen' },
  { min: 'minutes' },
  { h: 'hours' },
  { deg: 'degrees' },
  { pkt: 'packets' },
];

export const getIngredientUnitValueByKey = (
  key: string | undefined
): string | undefined => {
  if (key) {
    const mapping = ingredientsUnitMapping.find(
      (unit) => Object.keys(unit)[0] === key
    );

    return mapping ? mapping[key as keyof typeof mapping] : key;
  }
};

const convertToDecimal = (measurement: string) => {
  return new Fraction(measurement).simplify(0.01);
};

const numberString = (value: string): number => {
  const result =
    value.indexOf('/') !== -1 ? convertToDecimal(value.trim()) : value;
  return Number(result);
};

const roundInterval = (value: string, roundType: 'up' | 'down'): number => {
  const interval = 0.25;
  const numberVal = numberString(value);
  const factor =
    roundType === 'up'
      ? Math.ceil(numberVal / interval)
      : Math.floor(numberVal / interval);
  const result = factor * interval;
  return parseFloat(result.toFixed(2));
};

const getFraction = (amount: string, roundType: 'up' | 'down'): string => {
  const num = Number(amount);

  if (isNaN(num)) {
    return amount;
  }

  const whole = Math.floor(num);
  const decimal = Number((num - whole).toFixed(2));

  // 1. Handle specific "snap" cases for 1/3 and 2/3
  if (decimal >= 0.3 && decimal <= 0.4) {
    return whole === 0 ? '1/3' : `${whole} 1/3`;
  }

  if (decimal >= 0.59 && decimal <= 0.7) {
    return whole === 0 ? '2/3' : `${whole} 2/3`;
  }

  // 2. Determine if we should force a "down" round based on specific ranges
  const effectiveRoundType =
    (decimal >= 0.75 && decimal <= 0.85) ||
    (whole > 0 &&
      ((decimal >= 0.01 && decimal <= 0.1) ||
        (decimal >= 0.26 && decimal <= 0.29) ||
        (decimal >= 0.51 && decimal <= 0.58)))
      ? 'down'
      : roundType;

  // 3. Fallback to interval rounding and Fraction library
  const finalValue = roundInterval(amount, effectiveRoundType);

  return new Fraction(finalValue).simplify(0.001).toFraction(true);
};

const convertToIntegerString = (amount: string): string => {
  const num = Number(amount);

  // Return original if invalid or if it has a non-zero decimal component
  if (isNaN(num) || num % 1 !== 0) {
    return amount;
  }

  return num === 0 ? '' : `${num}`;
};

const convertToFractionString = (value: string): string => {
  const numberVal = parseFloat(value);

  if (isNaN(numberVal)) {
    return value;
  }

  return numberVal < 1
    ? getFraction(`${numberVal}`, 'up')
    : `${Math.round(numberVal)}`;
};

export const convertMeasurement = (
  amount: string,
  unit: string | undefined
): string => {
  if (!amount || amount === '') {
    return '';
  }
  unit ||= 'empty';
  switch (unit) {
    case 'kg':
    case 'cm':
    case 'L':
      return convertToIntegerString(amount);
    case 'g':
    case 'mg':
    case 'ml':
    case 'empty':
      return convertToFractionString(amount); // converts to fraction if below 1, else rounded integer
    default:
      return getFraction(amount, 'up'); // converts all to fraction
  }
};
